import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material'
import {
  Close,
  Timeline as TimelineIcon,
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  Assignment
} from '@mui/icons-material'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent
} from '@mui/lab'
import { format, parseISO } from 'date-fns'
import { useSnackbar } from 'notistack'
import mrdService from '../../../services/mrdservice'

const FileMovementHistoryDialog = ({ open, onClose, file }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  const [movements, setMovements] = useState([])

  useEffect(() => {
    if (open && file) {
      loadMovements()
    }
  }, [open, file])

  const loadMovements = async () => {
    setLoading(true)
    try {
      const response = await mrdService.getFileMovements(file._id)
      
      if (response.data.success) {
        setMovements(response.data.movements || [])
      }
    } catch (error) {
      console.error('Error loading movements:', error)
      enqueueSnackbar('Failed to load movement history', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const getMovementColor = (movementType) => {
    const colorMap = {
      'REQUEST_CREATED': 'warning',
      'REQUEST_APPROVED': 'info',
      'REQUEST_ISSUED': 'primary',
      'FILE_RETURNED': 'success',
      'MANUAL_MOVEMENT': 'default'
    }
    return colorMap[movementType] || 'default'
  }

  const getMovementIcon = (movementType) => {
    const iconMap = {
      'REQUEST_CREATED': <Assignment fontSize="small" />,
      'REQUEST_APPROVED': <CheckCircle fontSize="small" />,
      'REQUEST_ISSUED': <TimelineIcon fontSize="small" />,
      'FILE_RETURNED': <CheckCircle fontSize="small" />,
      'MANUAL_MOVEMENT': <Schedule fontSize="small" />
    }
    return iconMap[movementType] || <Schedule fontSize="small" />
  }

  const getMovementLabel = (movementType) => {
    const labelMap = {
      'REQUEST_CREATED': 'Request Created',
      'REQUEST_APPROVED': 'Request Approved',
      'REQUEST_ISSUED': 'Request Issued',
      'FILE_RETURNED': 'File Returned',
      'MANUAL_MOVEMENT': 'Manual Movement'
    }
    return labelMap[movementType] || movementType
  }

  const getStatusChip = (status) => {
    const statusConfig = {
      'AVAILABLE': { color: 'success', label: 'Available' },
      'REQUESTED': { color: 'warning', label: 'Requested' },
      'APPROVED': { color: 'info', label: 'Approved' },
      'ISSUED': { color: 'primary', label: 'Issued' }
    }
    
    const config = statusConfig[status] || { color: 'default', label: status }
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    )
  }

  if (!file) return null

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Movement History
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {file.ipdNumber || file.fileNumber} - {file.patientName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : movements.length === 0 ? (
          <Alert severity="info">
            No movement history found for this file.
          </Alert>
        ) : (
          <Timeline position="right">
            {movements.map((movement, index) => (
              <TimelineItem key={movement._id}>
                <TimelineOppositeContent color="text.secondary" sx={{ flex: 0.3 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {format(parseISO(movement.processedAt || movement.createdAt), 'MMM dd, yyyy')}
                  </Typography>
                  <Typography variant="caption">
                    {format(parseISO(movement.processedAt || movement.createdAt), 'HH:mm')}
                  </Typography>
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineDot color={getMovementColor(movement.movementType)}>
                    {getMovementIcon(movement.movementType)}
                  </TimelineDot>
                  {index < movements.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {getMovementLabel(movement.movementType)}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      {getStatusChip(movement.previousStatus)}
                      <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                        →
                      </Typography>
                      {getStatusChip(movement.newStatus)}
                    </Box>

                    {movement.relatedRequest && (
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={`Request #${movement.relatedRequest.requestNumber}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                        {movement.relatedRequest.requestType && (
                          <Chip
                            label={movement.relatedRequest.requestType}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    )}

                    {movement.location && (
                      <Typography variant="body2" color="text.secondary">
                        📍 Location: {movement.location}
                      </Typography>
                    )}

                    {movement.issuedTo && (
                      <Typography variant="body2" color="text.secondary">
                        👤 Issued To: {movement.issuedTo}
                      </Typography>
                    )}

                    {movement.processedBy && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Processed by: {movement.processedBy.name}
                      </Typography>
                    )}

                    {movement.remarks && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        "{movement.remarks}"
                      </Typography>
                    )}
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FileMovementHistoryDialog